import {
  Box,
  Button,
  Container,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  Grid,
  GridItem,
  ListItem,
  Spacer,
  Text,
  UnorderedList,
  useDisclosure,
} from "@chakra-ui/react";
import AppIdentitySelector from "@/features/ui/app-identity-selector";
import { Outlet } from "react-router";
import AppSignOutButton from "@/features/ui/app-sign-out-button";
import AppNavigationLogo from "@/features/ui/app-navigation/app-navigation-logo";
import AppDumbTransactionModal from "@/features/ui/dumb-transaction/app-dumb-transaction-modal";
import AppLanguageSelector from "@/features/ui/app-language-selector";
import useIdentity from "@/features/ui/hooks/use-identity";
import { useEffect, useRef, useState } from "react";
import useRouterHelper from "@/features/ui/hooks/use-router-helper";
import AppMyAccountContextMenu from "@/features/ui/app-my-account-context-menu";
import AppNavigation from "@/features/ui/app-navigation";
import * as amplitude from "@amplitude/analytics-browser";
import AppNavigationBottom from "@/features/ui/app-navigation-bottom";
import {
  BrowserView,
  MobileView,
  isBrowser,
  isMobile,
} from "react-device-detect";
import SVG from "react-inlinesvg";
import AppCompanySelection from "@/features/ui/app-company-selection";
import {
  applyCompanyIdFromUrl,
  needsCompanySelection,
} from "@/features/ui/helpers/identity-helpers";

export default function PrivateLayout() {
  // useIdentity() 가 세션 값을 읽기 전에 ?company_id=... 를 먼저 반영한다.
  applyCompanyIdFromUrl();

  const identity = useIdentity();
  const { navigate, location } = useRouterHelper();
  const [companyName, setCompanyName] = useState("");

  // useEffect(() => {
  //   const getComp = async () => {
  //     const res = await fetch(`${process.env.NEXT_PUBLIC_API_URL}/buyers`);
  //     const data = await res.json();
  //     console.log(data);
  //     setCompanyName(data?.name);
  //   };
  //   const data = getComp();
  // }, []);
  const scrollRef = useRef<HTMLDivElement>(null);
  const [hasScroll, setHasScroll] = useState(false);

  useEffect(() => {
    const checkScroll = () => {
      if (!scrollRef.current) return;
      const el = scrollRef.current;
      setHasScroll(el.scrollHeight > el.clientHeight);
    };

    const resizeObserver = new ResizeObserver(checkScroll);

    if (scrollRef.current) {
      resizeObserver.observe(scrollRef.current);
      checkScroll(); // 초기 체크
    }

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  useEffect(() => {
    if (identity === null) {
      //url을 바로 변경하면
      setTimeout(() => {
        //signOut시에 location변경을 해도 component의 state가 변경됨으로 rerender발생 -> 이 hook실행됨
        //을 방지하기위해서 hack사용
        if (sessionStorage.getItem("signOut")) {
          sessionStorage.removeItem("signOut");
          return;
        }

        const redirectUrl = encodeURIComponent(
          location.pathname + location.search
        );

        navigate("/auth/sign-in?redirect_url=" + redirectUrl, {
          replace: true,
        });
      }, 300);
    } else if (identity.company === null) {
      navigate("/auth/sign-up-pending", { replace: true });
    }
  }, [identity, location.pathname, location.search, navigate]);

  const { isOpen, onOpen, onClose } = useDisclosure();
  const btnRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (identity?.company?.id) {
      amplitude.setUserId(`${identity.company.id} + ${identity.company.type}`);
      // amplitude.identify();
    }
  }, [identity]);

  useEffect(() => {
    onClose();
  }, [location.pathname, onClose]);

  if (identity === null) {
    return null;
  }

  // 이번 세션에서 아직 회사를 고르지 않았다면, 첫 번째 회사로 넘기지 않고 선택부터 받는다.
  // 라우팅하지 않으므로 접속한 주소는 그대로 유지된다.
  if (needsCompanySelection()) {
    return <AppCompanySelection />;
  }

  return (
    <Container
      maxW="container.2xl"
      padding={"0"}
      background={"#FCFCFE"}
      position={"relative"}
      overflow={"hidden"}
    >
      <BrowserView>
        <Grid
          height="100vh"
          maxHeight={"100vh"}
          templateColumns={"198px 1fr"}
          templateRows="48px 1fr"
          padding={"0"}
        >
          <GridItem
            gridColumn={"1/3"}
            gridRow={"1/2"}
            borderBottom={"1px solid #EFEFED"}
          >
            <Flex
              alignItems="center"
              justifyContent="flex-end"
              gap="8px"
              h={"100%"}
            >
              <AppNavigationLogo collapsed={false} />
              <Spacer />
              <AppIdentitySelector />
              <AppLanguageSelector />
              <AppMyAccountContextMenu />
            </Flex>
          </GridItem>
          <GridItem
            gridColumn="1/2"
            gridRow="2/3"
            borderRight="1px solid #EFEFED"
            position="relative"
            zIndex="2"
            background="#FCFCFE"
            display="flex"
            flexDirection="column"
            height="95vh" // 또는 부모 그리드 높이에 맞게 100%면 됨
          >
            {/* 스크롤 되는 영역 */}
            <Box flex="1" overflowY="auto" minHeight="0" ref={scrollRef}>
              <AppNavigation />
            </Box>
            {/* 하단 고정 영역 */}
            <Box borderTop={hasScroll ? "1px solid #EFEFED" : "none"}>
              <AppNavigationBottom />
            </Box>
          </GridItem>
          <GridItem gridColumn={"2/3"} gridRow={"2/3"} overflow={"hidden"}>
            <Outlet />
          </GridItem>
        </Grid>
      </BrowserView>
      <MobileView>
        {/* PWA 설치 안내 배너가 뜨면 앱 셸 자체를 그만큼 줄인다. 내부 height:100% 체인이
            따라 줄어들어서, 스크롤 컨테이너마다 padding-bottom을 붙이지 않아도 된다.
            dvh는 쓰지 않는다 — browserslist의 safari 12가 지원하지 않는다. */}
        <Grid
          height="calc(100vh - var(--pwa-banner-height, 0px))"
          maxHeight={"calc(100vh - var(--pwa-banner-height, 0px))"}
          templateColumns={"1fr"}
          templateRows="48px 1fr"
          padding={"0"}
        >
          <GridItem gridRow={"1/2"} borderBottom={"1px solid #EFEFED"}>
            <Flex
              alignItems="center"
              justifyContent="space-between"
              gap="8px"
              h={"100%"}
              paddingRight={"16px"}
            >
              <AppNavigationLogo collapsed={false} />
              <Button variant={"ghost"} padding={0} onClick={onOpen}>
                <SVG src={"/icons-v1_1/menu.svg"} />
              </Button>
              <Drawer
                isOpen={isOpen}
                placement="right"
                onClose={onClose}
                finalFocusRef={btnRef}
                // Drawer도 Modal과 같은 스크롤 락을 쓰므로 열려 있는 동안 두 손가락
                // 제스처가 취소된다. 확대된 화면에서 축소로 빠져나올 수 있게 허용한다.
                allowPinchZoom={isMobile}
              >
                <DrawerOverlay />
                <DrawerContent>
                  <DrawerCloseButton />
                  <DrawerHeader>모바일 메뉴</DrawerHeader>

                  <DrawerBody>
                    {/*<UnorderedList>*/}
                    {/*  <ListItem>주문서 확인하기</ListItem>*/}
                    {/*</UnorderedList>*/}
                    <AppNavigation isMobile />
                  </DrawerBody>

                  <DrawerFooter
                    flexDirection={"column"}
                    alignItems={"flex-end"}
                    gap={"8px"}
                  >
                    <AppMyAccountContextMenu />
                    <AppIdentitySelector />
                    <AppLanguageSelector />
                  </DrawerFooter>
                </DrawerContent>
              </Drawer>
              {/*<Spacer />*/}
              {/*<AppIdentitySelector />*/}
              {/*<AppLanguageSelector />*/}
              {/*<AppMyAccountContextMenu />*/}
            </Flex>
          </GridItem>
          <GridItem gridRow={"2/3"} overflow={"hidden"}>
            <Outlet />
          </GridItem>
        </Grid>
      </MobileView>
      <AppDumbTransactionModal />
    </Container>
  );
}
